import React, { useMemo } from 'react';
import EditableTable from '../editableTable/editableTable';
import CustomModal from '../../../../../../../../components/custom-modal/custom-modal';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { IconButton } from '@mui/material';
import { FUNCTIONAL_SAFETY_CHANNEL_TYPES } from '../../../../../../../../utils/constants';
import useMTTF from '../../../../../../../../hooks/useMTTF';

// Channel Enum
const CHANNEL_TYPES = FUNCTIONAL_SAFETY_CHANNEL_TYPES;

const MTTFd = ({
  setShow,
  show,
  assessment,
  groupedMTTFData,
  createMTTF,
  updateMTTF,
  deleteMTTF,
  isCreating,
  isUpdating,
  isDeleting,
}) => {
  const {
    newRows,
    editingRow,
    editedData,
    shouldShowTeOte,
    shouldShowChannel2,
    hasUnsavedRows,
    mttfdChannel1,
    mttfdHalfChannel,
    mttfdChannel2,
    finalMTTFd,
    addComponent,
    handleNewRowChange,
    handleRemoveNewRow,
    handleSaveAllNewRows,
    handleStartEdit,
    handleEditChange,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete,
  } = useMTTF({
    assessment,
    groupedMTTFData,
    createMTTF,
    updateMTTF,
    deleteMTTF,
    isCreating,
    isUpdating,
    isDeleting,
  });

  const tableHeaders = [
    'Item',
    'Part Number',
    'Part Description',
    'MTTFi (Years)',
    'Dangerous Failure %',
    'MTTFd,i (Years)',
    'Quantity',
    'Action',
  ];

  const tableKeys = [
    'item',
    'part_number',
    'part_description',
    'mttfi',
    'dangerous_failure',
    'mttfdij',
    'quantity',
    'action',
  ];

  const generateTableData = type => {
    const savedRows = groupedMTTFData?.[type] || [];
    const unsavedRows = newRows[type] || [];

    const savedRowsData = savedRows.map((item, index) => {
      const isEditing = editingRow?.type === type && editingRow?.id === item.id;
      const rowData = isEditing ? editedData : item;

      return tableKeys.map(key => {
        const value = rowData[key];
        const inputType = ['mttfi', 'mttfdij', 'quantity', 'dangerous_failure'].includes(key) ? 'number' : 'text';

        if (key === 'action') {
          if (isEditing) {
            return (
              <div style={{display: 'flex', gap: '4px'}}>
                <IconButton
                  size="small"
                  onClick={handleSaveEdit}
                  disabled={isUpdating}
                  color="primary"
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={isUpdating}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </div>
            );
          }
          return (
            <div style={{display: 'flex', gap: '4px'}}>
              <IconButton
                size="small"
                onClick={() => handleStartEdit(type, item)}
                disabled={isDeleting}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(item.id)}
                disabled={isDeleting}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        }

        if (key === 'item') {
          return index + 1;
        }

        if (isEditing && key !== 'mttfdij') {
          return (
            <input
              disabled={key === 'mttfdij'}
              type={inputType}
              value={value || ''}
              onChange={e => {
                const newValue = e.target.value;
                if (inputType === 'number' && newValue < 0) return;
                if(key==="quantity"){
                  const num = parseInt(newValue);
                  if (newValue !== '' && num < 1) {
                    return;
                  }
                }
                if (key === 'dangerous_failure') {
                  const num = parseFloat(newValue);
                  if (newValue !== '' && (isNaN(num) || num < 1 || num > 100)) {
                    return;
                  }
                }
                handleEditChange(key, newValue);
              }}
              style={{width: '100%'}}
            />
          );
        }

        return value || '-';
      });
    });

    const unsavedRowsData = unsavedRows.map((item, index) => {
      const absoluteIndex = savedRows.length + index;

      return tableKeys.map(key => {
        const value = item[key];
        const inputType = ['mttfi', 'mttfdij', 'quantity', 'dangerous_failure'].includes(key) ? 'number' : 'text';

        if (key === 'action') {
          return (
            <div style={{display: 'flex', gap: '4px'}}>
              <IconButton
                size="small"
                onClick={() => handleRemoveNewRow(type, item.tempId)}
                disabled={isCreating}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        }

        if (key === 'item') {
          return absoluteIndex + 1;
        }

        return (
          <input
          disabled={key === 'mttfdij'}
            type={inputType}
            value={value || ''}
            onChange={e => {
              const newValue = e.target.value;
              if (inputType === 'number') {
                if (newValue !== '' && (isNaN(newValue) || parseFloat(newValue) < 0)) {
                  return;
                }
                if(key==="quantity"){
                  const num = parseInt(newValue);
                  if (newValue !== '' && num < 1) {
                    return;
                  }
                }
                if (key === 'dangerous_failure') {
                  const num = parseFloat(newValue);
                  if (newValue !== '' && (num < 1 || num > 100)) {
                    return;
                  }
                }
              }
              handleNewRowChange(type, item.tempId, key, newValue);
            }}
            style={{width: '100%'}}
            min={inputType === 'number' ? (key === 'dangerous_failure' ? '1' : '0') : undefined}
            max={key === 'dangerous_failure' ? '100' : undefined}
            step={inputType === 'number' ? 'any' : undefined}
          />
        );
      });
    });

    return [...savedRowsData, ...unsavedRowsData];
  };

  // Channel 1 tables (Input, Logic, Output)
  const channel1Tables = useMemo(() => {
    return [
      {heading: 'Input (I1)', type: CHANNEL_TYPES.INPUT, data: generateTableData(CHANNEL_TYPES.INPUT)},
      {heading: 'Logic (L1)', type: CHANNEL_TYPES.LOGIC, data: generateTableData(CHANNEL_TYPES.LOGIC)},
      {heading: 'Output (O1)', type: CHANNEL_TYPES.OUTPUT, data: generateTableData(CHANNEL_TYPES.OUTPUT)},
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedMTTFData, shouldShowTeOte, editingRow?.type, editingRow?.id, newRows, editedData]);

  // Half Channel tables (TE, OTE)
  const halfChannelTables = useMemo(() => {
    if (!shouldShowTeOte) {
      return [];
    }
    return [
        {heading: 'Test Equipment (TE)', type: CHANNEL_TYPES.TE, data: generateTableData(CHANNEL_TYPES.TE)},
        {heading: 'Other Test Equipment (OTE)', type: CHANNEL_TYPES.OTE, data: generateTableData(CHANNEL_TYPES.OTE)}
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedMTTFData, shouldShowTeOte, editingRow?.type, editingRow?.id, newRows, editedData]);

  // Channel 2 tables (Input2, Logic2, Output2)
  const channel2Tables = useMemo(() => {
    if (!shouldShowChannel2) {
      return [];
    }
    return [
      {heading: 'Input (I2)', type: CHANNEL_TYPES.INPUT2, data: generateTableData(CHANNEL_TYPES.INPUT2)},
      {heading: 'Logic (L2)', type: CHANNEL_TYPES.LOGIC2, data: generateTableData(CHANNEL_TYPES.LOGIC2)},
      {heading: 'Output (O2)', type: CHANNEL_TYPES.OUTPUT2, data: generateTableData(CHANNEL_TYPES.OUTPUT2)}
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedMTTFData, shouldShowChannel2, editingRow?.type, editingRow?.id, newRows, editedData]);

  return (
    <>
      <CustomModal title="MTTFd" show={show} setShow={setShow}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            justifyItems: 'start',
            textAlign: 'start',
            fontSize: '18px',
            marginBottom: '16px',
          }}>
          <span>Mean Time To Dangerous Failure Assessment</span>
          <span>Control System Category: Category {assessment?.category}</span>
          
          {/* All Channel MTTFd Scores at Top */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
            {/* Channel 1 MTTFd */}
            {mttfdChannel1?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '5px',
                border: '1px solid #2196f3'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                  Channel 1 MTTFd: {mttfdChannel1?.value?.toFixed(2)} Years
                </span>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                  Channel 1 MTTFd Category: {mttfdChannel1?.mttfCategory || 'N/A'}
                </span>
              </div>
            )}

            {/* Half Channel MTTFd */}
            {shouldShowTeOte && mttfdHalfChannel?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#fff3e0',
                borderRadius: '5px',
                border: '1px solid #ff9800'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#e65100'}}>
                  Half Channel MTTFd: {mttfdHalfChannel?.value?.toFixed(2)} Years
                </span>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#e65100'}}>
                  Half Channel MTTFd Category: {mttfdHalfChannel?.mttfCategory || 'N/A'}
                </span>
              </div>
            )}

            {/* Channel 2 MTTFd */}
            {shouldShowChannel2 && mttfdChannel2?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#f3e5f5',
                borderRadius: '5px',
                border: '1px solid #9c27b0'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#6a1b9a'}}>
                  Channel 2 MTTFd: {mttfdChannel2?.value?.toFixed(2)} Years
                </span>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#6a1b9a'}}>
                  Channel 2 MTTFd Category: {mttfdChannel2?.mttfCategory || 'N/A'}
                </span>
              </div>
            )}

            {/* Final MTTFd (when multiple channels exist) */}
            {((shouldShowTeOte && mttfdHalfChannel?.value > 0) || (shouldShowChannel2 && mttfdChannel2?.value > 0)) && finalMTTFd?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#e8f5e9',
                borderRadius: '5px',
                border: '2px solid #4caf50'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '18px', color: '#2e7d32'}}>
                  Final MTTFd: {finalMTTFd?.value?.toFixed(2)} Years
                </span>
                <span style={{fontWeight: 'bold', fontSize: '18px', color: '#2e7d32'}}>
                  Final MTTFd Category: {finalMTTFd?.mttfCategory || 'N/A'}
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
          {/* Channel 1 Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
            <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem'}}>Channel 1</h3>
            {channel1Tables?.map((table, index) => (
            <div
                key={`channel1-${index}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                border: '0.5px solid gray',
                padding: '7px',
                borderRadius: '7px',
                width: '100%',
              }}>
              <EditableTable
                actionButtons={[
                  {
                    text: 'Add Component',
                    clickHandler: () => addComponent(table?.type),
                  },
                ]}
                tableHeading={table?.heading}
                headers={tableHeaders}
                data={table?.data}
              />
            </div>
          ))}
            <div className="text-center" style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
              <span style={{fontWeight: 'bold'}}>Channel 1 MTTFd: {mttfdChannel1?.value?.toFixed(2)} Years</span>
              <span style={{fontWeight: 'bold'}}>Channel 1 MTTFd Category: {mttfdChannel1?.mttfCategory || 'N/A'}</span>
              {/* {(shouldShowTeOte && mttfdHalfChannel?.value > 0) || (shouldShowChannel2 && mttfdChannel2?.value > 0) ? (
                <div style={{marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#e3f2fd', borderRadius: '4px'}}>
                  <span style={{fontWeight: 'bold', color: '#1976d2'}}>Final MTTFd: {finalMTTFd?.value?.toFixed(2)} Years</span>
                  <span style={{fontWeight: 'bold', color: '#1976d2', marginLeft: '1rem'}}>Category: {finalMTTFd?.mttfCategory || 'N/A'}</span>
                </div>
              ) : null} */}
            </div>
          </div>

          {/* Half Channel Section (TE, OTE) */}
          {shouldShowTeOte && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #ccc'}}>
              <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem'}}>Half Channel</h3>
              {halfChannelTables?.map((table, index) => (
                <div
                  key={`halfchannel-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '0.5px solid gray',
                    padding: '7px',
                    borderRadius: '7px',
                    width: '100%',
                  }}>
                  <EditableTable
                    actionButtons={[
                      {
                        text: 'Add Component',
                        clickHandler: () => addComponent(table?.type),
                      },
                    ]}
                    tableHeading={table?.heading}
                    headers={tableHeaders}
                    data={table?.data}
                  />
                </div>
              ))}
              <div className="text-center" style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                <span style={{fontWeight: 'bold'}}>Half Channel MTTFd: {mttfdHalfChannel?.value?.toFixed(2)} Years</span>
                <span style={{fontWeight: 'bold'}}>Half Channel MTTFd Category: {mttfdHalfChannel?.mttfCategory || 'N/A'}</span>
              </div>
            </div>
          )}

          {/* Channel 2 Section */}
          {shouldShowChannel2 && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #ccc'}}>
              <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem'}}>Channel 2</h3>
              {channel2Tables?.map((table, index) => (
                <div
                  key={`channel2-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '0.5px solid gray',
                    padding: '7px',
                    borderRadius: '7px',
                    width: '100%',
                  }}>
                  <EditableTable
                    actionButtons={[
                      {
                        text: 'Add Component',
                        clickHandler: () => addComponent(table?.type),
                      },
                    ]}
                    tableHeading={table?.heading}
                    headers={tableHeaders}
                    data={table?.data}
                  />
                </div>
              ))}
              <div className="text-center" style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                <span style={{fontWeight: 'bold'}}>Channel 2 MTTFd: {mttfdChannel2?.value?.toFixed(2)} Years</span>
                <span style={{fontWeight: 'bold'}}>Channel 2 MTTFd Category: {mttfdChannel2?.mttfCategory || 'N/A'}</span>
              </div>
            </div>
          )}

          {hasUnsavedRows && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              marginTop: '1rem',
              padding: '1rem',
              borderTop: '1px solid #ddd'
            }}>
              <button
                onClick={handleSaveAllNewRows}
                disabled={isCreating}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: isCreating ? 0.6 : 1,
                }}
              >
                {isCreating ? 'Saving...' : 'Save All'}
              </button>
            </div>
          )}
        </div>
      </CustomModal>
    </>
  );
};

export default MTTFd;
